"use client"

import { ChevronLeft, X } from "lucide-react"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"
import { Manrope } from "next/font/google"
import { cn } from "@/lib/utils"

const manrope = Manrope({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700", "800"],
})

interface ModalMobileProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  children: React.ReactNode
  height?: string
  trigger?: React.ReactNode
  hasDragAndDrop?: boolean
  onBack?: () => void
  disableSwipeClose?: boolean
  showBackArrow?: boolean
  contentClassName?: string
  onAnimationEnd?: (open: boolean) => void
}

export function ModalMobile({
  open,
  onOpenChange,
  title,
  height = "85vh",
  children,
  trigger,
  hasDragAndDrop = false,
  onBack,
  disableSwipeClose = true,
  showBackArrow = true,
  contentClassName,
  onAnimationEnd,
}: ModalMobileProps) {
  // dvh (dynamic viewport height) ikut menyusut natural saat keyboard mobile muncul,
  // beda dengan vh yang diam di ukuran viewport awal
  const dvhHeight = height.endsWith("vh") ? height.replace(/vh$/, "dvh") : height

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      onAnimationEnd={onAnimationEnd}
      handleOnly={disableSwipeClose}
      dismissible
    >
      {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}

      <DrawerContent
        className={`${manrope.className} !z-[50]  flex flex-col rounded-t-[20px] bg-white dark:bg-gray-800 focus:outline-none`}
        style={{ height: dvhHeight }}
      >
        <DrawerHeader className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="relative flex items-center justify-between">
            <div className="flex gap-2 items-center">
              {showBackArrow &&
                <Button
                  onClick={() => (onBack ? onBack() : onOpenChange(false))}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors h-8 w-8 shadow-none"
                  aria-label="Back"
                  variant={"ghost"}
                  size="icon"
                >
                  <ChevronLeft size={20} className="text-gray-700 dark:text-gray-200" />
                </Button>
              }
              <DrawerTitle className="text-sm font-bold text-gray-900 dark:text-white">
                {title}
              </DrawerTitle>
            </div>

            <Button
              onClick={() => onOpenChange(false)}
              className="absolute right-0 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors h-8 w-8 shadow-none"
              aria-label="Close"
              variant={"ghost"}
              size="icon"
            >
              <X size={20} className="text-gray-500 dark:text-gray-400" />
            </Button>
          </div>
        </DrawerHeader>

        <div
          className={cn("flex-1 relative overflow-y-auto", contentClassName)}
          data-vaul-no-drag={hasDragAndDrop || undefined}
          data-modal-scroll
        >
          {children}
        </div>
      </DrawerContent>
    </Drawer>
  )
}